"use client";

import { useCallback, useRef } from "react";

import { useCompany } from "./useCompany";
import { FEATURE_FLAGS } from ".constants/featureFlags";

/**
 * Паттерны вибрации для различных типов уведомлений.
 * @constant
 * @type {Object}
 * @property {number} info - Длительность одиночной вибрации для информационных уведомлений (200 мс).
 * @property {number[]} error - Массив длительностей вибрации для уведомлений об ошибках ([200, 100, 200] мс).
 */
export const VIBRATE_PATTERN = {
  info: 200,
  error: [200, 100, 200] as number[],
} as const;

/**
 * Тип для ключей паттернов вибрации.
 * @typedef {keyof typeof VIBRATE_PATTERN} TVibratePattern
 */
type TVibratePattern = keyof typeof VIBRATE_PATTERN;

/**
 * Интерфейс для параметров уведомления.
 * @interface INativeNotificationParams
 * @extends NotificationOptions
 * @property {string} id - Уникальный идентификатор уведомления.
 * @property {string} title - Заголовок уведомления.
 * @property {TVibratePattern} [vibrateType] - Тип вибрации (опционально).
 * @property {boolean} [isForLeaveTab] - Флаг, указывающий, что уведомление предназначено для неактивной вкладки (опционально).
 */
interface INativeNotificationParams extends NotificationOptions {
  id: string;
  title: string;
  vibrateType?: TVibratePattern;
  isForLeaveTab?: boolean;
}

/**
 * Интерфейс для возвращаемого значения хука.
 * @interface INativeNotification
 * @property {function} handleVibrate - Функция для запуска вибрации.
 * @property {function} pushNotification - Функция для отправки уведомления.
 */
interface INativeNotification {
  handleVibrate: (type?: TVibratePattern) => void;
  pushNotification: (params: INativeNotificationParams) => Promise<void>;
}

/**
 * Хук для работы с нативными уведомлениями и вибрацией.
 * @function useNativeNotifications
 * @returns {INativeNotification} Объект с методами для управления уведомлениями и вибрацией.
 */
export const useNativeNotifications = (): INativeNotification => {
  const { companyLogo } = useCompany();
  const notificationQueue = useRef<INativeNotificationParams[]>([]);
  const isProcessing = useRef(false);
  const notifiedIds = useRef<Set<string>>(new Set());

  /**
   * Функция для запуска вибрации устройства.
   * @function handleVibrate
   * @param {TVibratePattern} [vibrateType="info"] - Тип вибрации (по умолчанию "info").
   */
  const handleVibrate = useCallback((vibrateType: TVibratePattern = "info") => {
    // Проверяем, включены ли соответствующие флаги
    if (!FEATURE_FLAGS.IS_DEVICE_VIBRATION_ENABLED) {
      return;
    }

    // Проверяем, поддерживается ли вибрация устройством
    if ("vibrate" in navigator) {
      navigator.vibrate(VIBRATE_PATTERN[vibrateType]);
    } else {
      console.error("[ERROR]: Vibration is not supported on this device");
    }
  }, []);

  /**
   * Функция для обработки очереди уведомлений.
   * @function processQueue
   * @async
   */
  const processQueue = useCallback(async () => {
    // Проверяем, не выполняется ли уже обработка или пуста ли очередь уведомлений
    if (isProcessing.current || notificationQueue.current.length === 0) {
      return;
    }

    // Устанавливаем флаг, что обработка началась
    isProcessing.current = true;
    // Извлекаем первое уведомление из очереди
    const params = notificationQueue.current.shift()!;

    try {
      // Логируем ID уведомления для отладки
      console.log("[DEBUG]: Processing notification ID:", params.id);

      // Проверяем, предназначено ли уведомление для неактивной вкладки
      // Если вкладка активна, пропускаем уведомление
      if (params.isForLeaveTab && document.visibilityState !== "hidden") {
        console.log("[DEBUG]: Skipped notification due to visible tab");
        return;
      }

      // Проверяем, поддерживаются ли уведомления в браузере
      if (!("Notification" in window)) {
        console.error(
          "[ERROR]: Notifications are not supported in this browser",
        );
        return;
      }

      // Проверяем, заблокированы ли уведомления пользователем
      if (Notification.permission === "denied") {
        console.warn("[ERROR]: Notifications are blocked by user");
        return;
      }

      // Если разрешение на уведомления не получено, запрашиваем его
      if (Notification.permission !== "granted") {
        const permission = await Notification.requestPermission();
        console.log("[DEBUG]: Notification permission:", permission);
        if (permission !== "granted") {
          console.warn("[ERROR]: Notifications are not allowed");
          return;
        }
      }

      // Определяем источник логотипа для значка и иконки уведомления
      const logoSrc =
        typeof companyLogo === "string" ? companyLogo : companyLogo.src;

      // Создаём новое уведомление с параметрами
      new Notification(params.title, {
        badge: params.badge || logoSrc,
        icon: params.icon || logoSrc,
        ...params,
      });

      // Добавляем ID уведомления в список обработанных
      notifiedIds.current.add(params.id);

      // Запускаем вибрацию с указанным типом или по умолчанию "info"
      handleVibrate(params.vibrateType || "info");
    } catch (error) {
      // Логируем любую ошибку, возникшую при отправке уведомления
      console.error("[ERROR]: Failed to send notification:", error);
    } finally {
      // Сбрасываем флаг обработки
      isProcessing.current = false;
      // Рекурсивно вызываем обработку следующего уведомления в очереди
      processQueue();
    }
  }, [companyLogo, handleVibrate]);

  /**
   * Функция для добавления уведомления в очередь.
   * @function pushNotification
   * @async
   * @param {INativeNotificationParams} params - Параметры уведомления.
   */
  const pushNotification = useCallback(
    async (params: INativeNotificationParams) => {
      // Проверяем, включены ли соответствующие флаги
      if (!FEATURE_FLAGS.IS_BROWSER_NOTIFICATIONS_ENABLED) {
        return;
      }

      // Проверяем, не было ли уже отправлено уведомление с таким ID
      if (notifiedIds.current.has(params.id)) {
        console.log("[DEBUG]: Skipping duplicate notification ID:", params.id);
        return;
      }
      console.log("[DEBUG]: Adding notification to queue ID:", params.id);
      // Добавляем уведомление в очередь
      notificationQueue.current.push(params);
      processQueue();
    },
    [processQueue],
  );

  return { handleVibrate, pushNotification };
};
